import {
  useGetUserByEmailQuery,
  useUpdateUserMutation,
} from "store/apis/usersApi";
import { selectUser } from "store/slices/session/sessionSelector";
import { setUserDb } from "store/slices/session/sessionSlice";
import { selectLanguage } from "store/slices/app/appSelector";
import { setLanguage } from "store/slices/app/appSlice";
import { LanguageEnum } from "store/slices/app/appTypes";
import { useThemeMode } from "../theme/ThemeModeProvider";
import { PaletteMode } from "@mui/material";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

const isLanguage = (value?: string | null): value is LanguageEnum =>
  value === LanguageEnum.EN || value === LanguageEnum.ES;

const isThemeMode = (value?: string | null): value is PaletteMode =>
  value === "light" || value === "dark";

export const useGetMyUserQuery = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const language = useSelector(selectLanguage);
  const { mode, setMode } = useThemeMode();
  const [updateUser] = useUpdateUserMutation();
  const { data: userData } = useGetUserByEmailQuery(user?.email || "", {
    skip: !user?.email,
  });

  // Ajustes personales que sabemos guardados en la base para el usuario de la sesión
  const synced = useRef<{
    userId: string;
    language: LanguageEnum;
    themeMode: PaletteMode;
  } | null>(null);

  useEffect(() => {
    if (user && userData) {
      dispatch(setUserDb(userData));
    }
  }, [dispatch, user, userData]);

  // Base -> app: lo guardado manda (un admin puede haberlo cambiado). Si aún no hay
  // nada guardado, se sube lo que el usuario tenga en este navegador.
  useEffect(() => {
    if (!user || !userData?.id) {
      synced.current = null;
      return;
    }
    const nextLanguage = isLanguage(userData.language)
      ? userData.language
      : language;
    const nextMode = isThemeMode(userData.themeMode)
      ? userData.themeMode
      : mode;
    synced.current = {
      userId: userData.id,
      language: nextLanguage,
      themeMode: nextMode,
    };
    if (nextLanguage !== language) dispatch(setLanguage(nextLanguage));
    if (nextMode !== mode) setMode(nextMode);
    if (
      nextLanguage !== userData.language ||
      nextMode !== userData.themeMode
    ) {
      updateUser({
        id: userData.id,
        updates: { language: nextLanguage, themeMode: nextMode },
      });
    }
    // Solo al llegar datos del servidor; los cambios locales los guarda el efecto de abajo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userData]);

  // App -> base: el usuario cambia idioma o tema desde Settings o el sidebar. Solo ante
  // un cambio real: al montar, los valores locales aún no reflejan los del servidor.
  const previousLocal = useRef({ language, mode });
  useEffect(() => {
    const changed =
      previousLocal.current.language !== language ||
      previousLocal.current.mode !== mode;
    previousLocal.current = { language, mode };
    const current = synced.current;
    if (!changed || !current) return;
    if (current.language === language && current.themeMode === mode) return;
    synced.current = { ...current, language, themeMode: mode };
    updateUser({
      id: current.userId,
      updates: { language, themeMode: mode },
    });
  }, [language, mode, updateUser]);
};
